import { Permission, RFIStatus } from '@cims-online/shared'
import { ExpandOptions } from 'components/CIMSTable/CIMSTableContext/types/expand'
import { useRFITrackerContext } from '../../../../RFITrackerContext'
import { HelpMessage } from 'utils/constants'
import { dateFormatter } from 'utils/dateFormatter'
import Responses from './components/Responses'
import { useCreateRfiCommentMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { useAuthContext } from 'context/AuthContext'
import { Typography } from 'antd'
import { RequestForInformation } from '../../../../RFITrackerContext/types'

const Help = HelpMessage.RequestsForInformation.RFITracker

export function useExpandOptions(): ExpandOptions<RequestForInformation> {
  const { user } = useAuthContext()
  const { refetch, metadata, edit } = useRFITrackerContext()
  const commentPermission = useCheckPermission(
    Permission.CommentOnRequestForInformation
  )
  const [createComment] = useCreateRfiCommentMutation({
    ...DefaultMutationHookOptions,
  })
  return {
    render: ({
      id,
      description,
      submittedDate,
      closedDate,
      responses,
      responseDisciplines,
      comments,
      customFields,
      statusId,
      deleted,
    }) => {
      return {
        additionalInfo: [
          {
            label: 'Submitted Date',
            children: dateFormatter.short(submittedDate),
            help: Help.SubmittedDate,
          },
          {
            label: 'Closed Date',
            children: dateFormatter.short(closedDate),
            help: Help.ClosedDate,
          },
          {
            label: 'Description',
            children: (
              <Typography style={{ maxWidth: 725 }}>{description}</Typography>
            ),
            help: Help.FullDescription,
          },
          {
            label: 'Responses',
            children: responses.length ? (
              <Responses
                responses={responses}
                disciplines={responseDisciplines}
              />
            ) : null,
            help: Help.Responses,
          },
        ],
        comments: {
          comments: comments,
          permission:
            commentPermission ||
            !!user?.implicitlyAuthorized({
              users: metadata?.rfiResponseDisciplines.flatMap(
                ({ users }) => users
              ),
            }).authorized,
          onSave: async (text) => {
            await createComment({
              variables: {
                createRfiCommentInput: {
                  requestForInformationId: id,
                  text,
                },
              },
            })
            refetch()
          },
        },
        customFields: {
          customFields,
          alreadyDisabled:
            statusId === RFIStatus.Closed.id
              ? {
                  why: 'invalid status',
                  statusName: RFIStatus.Closed.name,
                }
              : false,
          recordInactive: deleted,
          booleanValueOptions: {
            onCheck: async (input) => {
              await edit({
                variables: {
                  editRequestForInformationInput: {
                    id,
                    description,
                    customFields: { booleanInputs: [input] },
                  },
                },
              })
              refetch()
            },
          },
          listValueOptions: {
            onCheck: async (input) => {
              await edit({
                variables: {
                  editRequestForInformationInput: {
                    id,
                    description,
                    customFields: { listInputs: { checkboxes: [input] } },
                  },
                },
              })
              refetch()
            },
          },
        },
      }
    },
  }
}
